import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { TITLE, SITE_URL } from '../config/domain';

interface SEOMetaData {
  title: string;
  description: string;
  keywords: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterImage?: string;
  twitterSite?: string;
  twitterCreator?: string;
  canonicalUrl?: string;
  robots?: string;
  hreflang?: { lang: string; url: string }[];
}

export const useSEO = (data: SEOMetaData) => {
  useEffect(() => {
    // Title
    document.title = data.title;

    // Meta description
    updateMetaTag('meta[name="description"]', 'name', 'description', data.description);

    // Meta keywords
    updateMetaTag('meta[name="keywords"]', 'name', 'keywords', data.keywords.join(', '));

    // Canonical URL
    if (data.canonicalUrl) {
      updateLinkTag('link[rel="canonical"]', 'rel', 'canonical', data.canonicalUrl);
    }

    // Robots
    if (data.robots) {
      updateMetaTag('meta[name="robots"]', 'name', 'robots', data.robots);
    } else {
      removeMetaTag('meta[name="robots"]');
    }

    // Hreflang
    removeHreflangTags();
    if (data.hreflang && data.hreflang.length > 0) {
      data.hreflang.forEach(({ lang, url }) => {
        const tag = document.createElement('link');
        tag.setAttribute('rel', 'alternate');
        tag.setAttribute('hreflang', lang);
        tag.setAttribute('href', url);
        document.head.appendChild(tag);
      });
      const xDefaultTag = document.createElement('link');
      xDefaultTag.setAttribute('rel', 'alternate');
      xDefaultTag.setAttribute('hreflang', 'x-default');
      xDefaultTag.setAttribute('href', data.hreflang[0].url);
      document.head.appendChild(xDefaultTag);
    }

    // Open Graph tags
    const ogData = [
      { property: 'og:title', content: data.ogTitle || data.title },
      { property: 'og:description', content: data.ogDescription || data.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: TITLE },
      { property: 'og:locale', content: 'it_IT' }
    ];

    if (data.ogUrl) {
      ogData.push({ property: 'og:url', content: data.ogUrl });
    }
    if (data.ogImage) {
      ogData.push({ property: 'og:image', content: data.ogImage });
      ogData.push({ property: 'og:image:secure_url', content: data.ogImage });
    }

    ogData.forEach(({ property, content }) => {
      updateMetaTag(`meta[property="${property}"]`, 'property', property, content);
    });

    // Twitter Card
    const twitterData = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: data.ogTitle || data.title },
      { name: 'twitter:description', content: data.ogDescription || data.description }
    ];

    if (data.twitterImage) {
      twitterData.push({ name: 'twitter:image', content: data.twitterImage });
    }
    if (data.twitterSite) {
      twitterData.push({ name: 'twitter:site', content: data.twitterSite });
    }
    if (data.twitterCreator) {
      twitterData.push({ name: 'twitter:creator', content: data.twitterCreator });
    }

    twitterData.forEach(({ name, content }) => {
      updateMetaTag(`meta[name="${name}"]`, 'name', name, content);
    });

  }, [data]);
};

const updateMetaTag = (selector: string, attrName: string, attrValue: string, content: string) => {
  let tag = document.querySelector(selector);
  if (tag) {
    tag.setAttribute('content', content);
  } else {
    tag = document.createElement('meta');
    tag.setAttribute(attrName, attrValue);
    tag.setAttribute('content', content);
    document.head.appendChild(tag);
  }
};

const updateLinkTag = (selector: string, attrName: string, attrValue: string, href: string) => {
  let tag = document.querySelector(selector);
  if (tag) {
    tag.setAttribute('href', href);
  } else {
    tag = document.createElement('link');
    tag.setAttribute(attrName, attrValue);
    tag.setAttribute('href', href);
    document.head.appendChild(tag);
  }
};

const removeMetaTag = (selector: string) => {
  const tag = document.querySelector(selector);
  if (tag) {
    tag.remove();
  }
};

const removeHreflangTags = () => {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(tag => tag.remove());
};
